import { type SubmitEvent, useState } from "react";

import { type AnyApiError, ApiError, isSuccess } from "@/api/ApiResult";
import { useCrud } from "@/api/useCrud";
import { Alert } from "@/components/ui/Alert/Alert";
import { Button } from "@/components/ui/Button/Button";
import { Form } from "@/components/ui/Form/Form";
import { FormLayout } from "@/components/ui/Form/FormLayout";
import { InputField } from "@/components/ui/InputField/InputField";
import { t } from "@/i18n/translate";
import type { ACCOUNT_UPDATE_REQUEST_PATH, AccountUpdateRequest, LoginResponse } from "@/types/generated/openapi";
import { StringFormData } from "@/utils/stringFormData";

import { type UserValidationErrors, validateUpdateUser } from "@/utils/validateUserAccount";

interface FormError {
  title: string;
  message?: string;
}

interface AccountSetupFormProps {
  user: LoginResponse;
  onSaved: (user: LoginResponse) => void;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: TODO function should be refactored
export function AccountSetupForm({ user, onSaved }: AccountSetupFormProps) {
  const [showLoginSuccess, setShowLoginSuccess] = useState(true);
  const [validationErrors, setValidationErrors] = useState<UserValidationErrors | null>(null);

  const updatePath: ACCOUNT_UPDATE_REQUEST_PATH = "/api/account";
  const { update, isLoading } = useCrud<LoginResponse>({ updatePath });
  const [formError, setFormError] = useState<FormError | null>(null);
  const [apiError, setApiError] = useState<AnyApiError | null>(null);

  function hideLoginSuccess() {
    setShowLoginSuccess(false);
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    hideLoginSuccess();

    const formData = new StringFormData(event.currentTarget);
    const account: Required<AccountUpdateRequest> = {
      username: user.username,
      fullname: formData.getString("fullname"),
      password: formData.getString("password"),
    };

    const passwordRepeat = formData.getString("password_repeat");
    const errors = validateUpdateUser(account, passwordRepeat);

    if (Object.keys(errors).length > 0) {
      setFormError({ title: t("account.not_saved"), message: t("account.check_fields") });
      setValidationErrors(errors);
      return;
    }

    setFormError(null);
    setValidationErrors(null);

    void update(account).then((result) => {
      if (isSuccess(result)) {
        onSaved(result.data);
        return;
      }

      if (result instanceof ApiError) {
        switch (result.reference) {
          case "InvalidFullname":
            setFormError({
              title: t("error.api_error.InvalidFullname"),
              message: t("account.invalid_fullname"),
            });
            setValidationErrors({ fullname: t("account.invalid_fullname") });
            return;
          case "PasswordRejectionSameAsOld":
          case "PasswordRejectionSameAsUsername":
          case "PasswordRejectionTooShort":
            setFormError({ title: t("account.not_saved"), message: t("account.check_fields") });
            setValidationErrors({ password: t(`error.api_error.${result.reference}`) });
            return;
          default:
            setApiError(result);
            return;
        }
      }
    });
  }

  return (
    <>
      {showLoginSuccess && (
        <FormLayout.Alert>
          <Alert type="success" onClose={hideLoginSuccess}>
            <strong className="heading-md">{t("account.login_success")}</strong>
            <p>{t("account.setting_up_account")}</p>
          </Alert>
        </FormLayout.Alert>
      )}
      {formError && (
        <FormLayout.Alert>
          <Alert type="error">
            <strong className="heading-md">{formError.title}</strong>
            <p>{formError.message}</p>
          </Alert>
        </FormLayout.Alert>
      )}
      {apiError && (
        <FormLayout.Alert>
          <Alert type="error">
            <strong className="heading-md">{apiError.message}</strong>
          </Alert>
        </FormLayout.Alert>
      )}
      <Form title={t("account.personalize_account")} onSubmit={handleSubmit}>
        <FormLayout disabled={isLoading}>
          <FormLayout.Section>
            <InputField
              id="fullname"
              name="fullname"
              label={t("account.fullname")}
              subtext={t("account.fullname_subtext")}
              hint={t("account.fullname_hint")}
              autoComplete="name"
              maxLength={255}
              error={validationErrors?.fullname}
              defaultValue={user.fullname}
            />
            <InputField
              id="username"
              name="username"
              label={t("account.username")}
              hint={t("account.username_hint")}
              autoComplete="username"
              value={user.username}
              disabled
            />
            <InputField
              id="password"
              name="password"
              label={t("account.password_new")}
              hint={t("account.password_hint")}
              type="password"
              autoComplete="new-password"
              error={validationErrors?.password}
            />
            <InputField
              id="password_repeat"
              name="password_repeat"
              label={t("account.password_repeat")}
              type="password"
              autoComplete="new-password"
              error={validationErrors?.password_repeat}
            />
          </FormLayout.Section>
          <FormLayout.Controls>
            <Button type="submit">{t("save")}</Button>
          </FormLayout.Controls>
        </FormLayout>
      </Form>
    </>
  );
}
